/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { ApplyIdInfo } from '../../../models/apply-id-model';
import { ApplyConduitIdReturnCodes, ApplyEquipmentDeviceIdReturnCodes } from '../../../models/apply-id-return-codes';
import { Observable, Subscription } from 'rxjs';
import {
  APPLY_ID_FACADE_TOKEN,
  IApplyIdFacade,
} from '../../../../../../data-access/src/lib/domain/facades-models/apply-id-facade-interface';
import { ActivatedRoute } from '@angular/router';
import { GlobalConstants } from '../../../constants/global-constants';
import { AcrylicSurfaceService } from '../../../acrylic-surface/acrylic-surface.service';
import { AlertStatus, IAlert } from "../../../../../../../shared/ui/src/lib/components/alert/alert-types";

@Component({
  selector: 'sysque-apply-id',
  templateUrl: './apply-id.component.html',
  styleUrls: ['./apply-id.component.scss'],
  standalone: false
})
export class ApplyIdComponent implements OnInit, OnDestroy {
  @Output() close = new EventEmitter();
  public data: ApplyIdInfo = {
    textId: '',
    buttonName: '',
  };
  public componentTitle = '';
  public id = '';
  public isApplyButtonDisabled = true;
  public invalidSelection = false;
  public alertStatus: AlertStatus;
  public warningMessage: string;

  private alertHeight = 308;
  private nonAlertHeight = 210;

  private validSelection$: Observable<any>;
  private validSelectionSubscription: Subscription;
  public get alert(): IAlert {
    return {
      message: this.warningMessage,
      type: this.alertStatus,
      isDismissable: true,
    };
  }

  constructor(
    @Inject(APPLY_ID_FACADE_TOKEN) private applyIdFacade: IApplyIdFacade,
    private route: ActivatedRoute,
    private acrylicSurfaceService: AcrylicSurfaceService
  ) {}

  public ngOnInit(): void {
    this.hideAlert();
    this.data.buttonName = GlobalConstants.ApplyButton;
    this.route.data.subscribe((data) => {
      if (data.isConduitIdDialog === true) {
        this.data.textId = GlobalConstants.ConduitId;
        this.componentTitle = GlobalConstants.APPLY_CONDUIT_ID_DIALOG;
      } else if (data.isConduitIdDialog === false) {
        this.data.textId = GlobalConstants.EquipmentDeviceId;
        this.componentTitle = GlobalConstants.APPLY_EQUIPMENT_DEVICE_ID_DIALOG;
      }
    });

    this.verifySelection();
    window.addEventListener(GlobalConstants.STORAGE, this.handleStorageEvent.bind(this));
  }

  public closeWindow() {
    this.close.emit();
    this.acrylicSurfaceService.hide();
  }

  public onUserInputChanged(): void {
    this.validateApplyButton();
  }

  public handleStorageEvent(event: StorageEvent): void {
    if (event.key === GlobalConstants.ApplyIdResponseKey) {
      if (event.newValue === '') {
        return;
      }
      const response = JSON.parse(event.newValue);
      if (response == ApplyConduitIdReturnCodes.Success) {
        this.showAlert(GlobalConstants.ConduitIdSuccessfullyApplied, AlertStatus.Success);
        setTimeout(() => {
          this.applyIdFacade.closePopupAction();
          this.acrylicSurfaceService.hide();
        }, 2000);
      } else if (response == ApplyEquipmentDeviceIdReturnCodes.Success) {
        this.showAlert(GlobalConstants.EquipmentIdSuccessfullyApplied, AlertStatus.Success);
        setTimeout(() => {
          this.applyIdFacade.closePopupAction();
          this.acrylicSurfaceService.hide();
        }, 2000);
      } else if (
        response == ApplyConduitIdReturnCodes.NoSelection ||
        response == ApplyEquipmentDeviceIdReturnCodes.NoSelection
      ) {
        this.showAlert(GlobalConstants.SelectionInvalidSelectSomething, AlertStatus.Warning);
      } else if (response == ApplyConduitIdReturnCodes.NoConduitSelection) {
        this.showAlert(GlobalConstants.SelectionInvalidSelectConduit, AlertStatus.Warning);
      } else if (response == ApplyEquipmentDeviceIdReturnCodes.NoEquipmentDeviceSelection) {
        this.showAlert(GlobalConstants.SelectionInvalidSelectEquipment, AlertStatus.Warning);
      }
    }
  }

  public validateApplyButton(): void {
    this.isApplyButtonDisabled = !this.id || (this.id && this.id.length < 1) || (this.id && this.id.trim() === '');
    if (this.isApplyButtonDisabled) {
      this.showAlert(GlobalConstants.PleaseEnterValidId, AlertStatus.Info);
    } else {
      this.hideAlert();
    }
  }

  public showAlert(message: string, alertType: AlertStatus): void {
    this.applyIdFacade.windowResize(this.alertHeight);
    this.warningMessage = message;
    this.alertStatus = alertType;
  }

  public hideAlert(): void {
    this.applyIdFacade.windowResize(this.nonAlertHeight);
    this.warningMessage = '';
  }

  public verifySelection(): void {
    this.validSelection$ = this.applyIdFacade.validateApplySelection();
    this.validSelectionSubscription = this.validSelection$.subscribe((response) => {
      if (
        response == ApplyConduitIdReturnCodes.ValidSelection ||
        response == ApplyEquipmentDeviceIdReturnCodes.ValidSelection
      ) {
        this.hideAlert();
        this.invalidSelection = false;
      }  else if (
        response == ApplyConduitIdReturnCodes.NoSelection ||
        response == ApplyEquipmentDeviceIdReturnCodes.NoSelection
      ) {
        this.showAlert(GlobalConstants.SelectionInvalidSelectSomething, AlertStatus.Warning);
        this.invalidSelection = true;
      } else if (response == ApplyConduitIdReturnCodes.NoConduitSelection) {
        this.showAlert(GlobalConstants.SelectionInvalidSelectConduit, AlertStatus.Warning);
        this.invalidSelection = true;
      } else if (response == ApplyEquipmentDeviceIdReturnCodes.NoEquipmentDeviceSelection) {
        this.showAlert(GlobalConstants.SelectionInvalidSelectEquipment, AlertStatus.Warning);
        this.invalidSelection = true;
      }
    });
  }

  public applyId(): void {
    this.hideAlert();
    this.applyIdFacade.applyIdClick(this.id);
  }

  public onAlertCloseButtonClick(): void {
    this.hideAlert();
  }

  public ngOnDestroy(): void {
    window.removeEventListener(GlobalConstants.STORAGE, this.handleStorageEvent.bind(this));
    if (this.validSelectionSubscription) {
      this.validSelectionSubscription.unsubscribe();
    }
  }
}
